import type { TConstraints } from '@feathr/rachis';
import { isWretchError, wretch } from '@feathr/rachis';

import type { FacebookCampaign } from './campaigns';
import type { IDimensionDefinition } from './creatives_base';
import { CreativeClass, ValidFacebookDimensions } from './creatives_base';
import type { IFacebookAdPreviewRequest, IFacebookDisplayCreative } from './creatives_meta';
import { createMetaDimensionValidator } from './creatives_meta';
import { FacebookDisplayCreative } from './creatives_meta';

// =============================================================================
// Facebook Image Creative Types
// =============================================================================

export const VALID_META_IMAGE_FORMATS = ['image/jpeg', 'image/png'];

export interface IFacebookImageAdPreviewRequest extends IFacebookAdPreviewRequest {
  instagram_user_id: string;
}

export interface IFacebookImageCreative extends IFacebookDisplayCreative {
  readonly _cls: CreativeClass.FacebookImage;
}

// =============================================================================
// Facebook Image Creative Class
// =============================================================================

/**
 * FacebookImageCreative handles Meta/Facebook image creatives.
 * Supports JPG and PNG formats with Facebook-specific dimensions.
 */
export class FacebookImageCreative extends FacebookDisplayCreative<IFacebookImageCreative> {
  public override readonly className = 'FacebookImageCreative';

  public override get constraints(): TConstraints<IFacebookImageCreative> {
    return {
      ...super.constraints,
      _cls: {
        inclusion: [CreativeClass.FacebookImage],
      },
      mimetype: {
        inclusion: {
          within: VALID_META_IMAGE_FORMATS,
          message: '^File must be JPG or PNG format',
        },
      },
      width: {
        ...super.constraints.width,
        equality: createMetaDimensionValidator(ValidFacebookDimensions, 'width'),
      },
      height: {
        ...super.constraints.height,
        equality: createMetaDimensionValidator(ValidFacebookDimensions, 'height'),
      },
    };
  }

  public override getDefaults(): Partial<IFacebookImageCreative> {
    return {
      ...super.getDefaults(),
      _cls: CreativeClass.FacebookImage,
    };
  }

  public override getSpec(): IDimensionDefinition | undefined {
    const key =
      Object.keys(ValidFacebookDimensions).find(
        (dimensionKey) => ValidFacebookDimensions[dimensionKey].spec === this.get('spec'),
      ) ?? `${this.get('width')}x${this.get('height')}`;
    if (ValidFacebookDimensions.hasOwnProperty(key)) {
      return ValidFacebookDimensions[key];
    }
    return undefined;
  }

  public override getValidDimensions(): Record<string, IDimensionDefinition> {
    return ValidFacebookDimensions;
  }

  public async getFacebookAdPreview(adFormat: string, campaign: FacebookCampaign): Promise<string> {
    this.assertCollection(this.collection);

    const attributes: IFacebookImageAdPreviewRequest = {
      ad_format: adFormat,
      _cls: this.get('_cls'),
      facebook_page_id: campaign.get('facebook_page_id'),
      instagram_user_id: campaign.get('instagram_user_id'),
      message: this.get('message'),
      name: this.get('name'),
      caption: this.get('caption'),
      description: this.get('description'),
      url: this.get('url'),
      destination_url: this.get('destination_url') || campaign.get('destination_url'),
      call_to_action_type: this.get('call_to_action_type'),
    };

    const url = `${this.collection.getHostname()}integrations/facebook/ad_preview`;
    const response = await wretch<{ body: string }>(url, {
      method: 'POST',
      body: JSON.stringify(attributes),
      headers: this.collection.getHeaders(),
    });
    if (isWretchError(response)) {
      throw response.error;
    }

    return response.data.body;
  }
}
